<template>
  <div class="boarding-page">
    <div class="page-header">
      <span class="page-title">寄宿托养订单管理</span>
    </div>

    <div class="info-card mt20">
      <div class="info-card-head">
        <span class="info-card-title">筛选查询</span>
      </div>
      <el-form :inline="true" ref="search" :model="search" class="mt15">
        <el-form-item prop="user_name_or_tel">
          <el-input v-model="search.user_name_or_tel" placeholder="被服务人姓名/手机号" clearable></el-input>
        </el-form-item>
        <el-form-item prop="order_no">
          <el-input v-model="search.order_no" placeholder="订单编号" clearable></el-input>
        </el-form-item>
        <el-form-item prop="nurse_name_or_tel">
          <el-input v-model="search.nurse_name_or_tel" placeholder="护工姓名/手机号" clearable></el-input>
        </el-form-item>
        <el-form-item prop="order_status">
          <el-select v-model="search.order_status" placeholder="订单状态" clearable>
            <el-option label="全部" value=""></el-option>
            <el-option v-for="option in BoardingOrderStatus" :key="option.val" :label="option.label"
                       :value="option.val"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item prop="cri_type">
          <el-select v-model="search.cri_type" placeholder="服务类型" clearable>
            <el-option label="日间照料" value="1"></el-option>
            <el-option label="寄宿托养" value="2"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item prop="rangeDate">
          <el-date-picker
            v-model="search.rangeDate"
            type="daterange"
            align="right"
            unlink-panels
            value-format="timestamp"
            format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="创建订单开始时间"
            end-placeholder="创建订单结束时间"
            :picker-options="pickerOptions"
            style="width: 360px"
          >
          </el-date-picker>
        </el-form-item>
        <el-form-item prop="serveRange">
          <el-date-picker
            v-model="search.serveRange"
            type="daterange"
            align="right"
            unlink-panels
            value-format="timestamp"
            format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="最近一次服务时间开始"
            end-placeholder="最近一次服务时间结束"
            :picker-options="pickerOptions"
            style="width: 420px"
          >
          </el-date-picker>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="searchData">查询</el-button>
          <el-button icon="el-icon-refresh" @click="resetForm('search')">重置</el-button>
          <el-button v-if="canCreateOrder" type="success" icon="el-icon-plus" @click="handleCreateOrder">创建订单</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="info-card mt20">
      <div class="info-card-head">
        <span class="info-card-title">订单列表</span>
      </div>
      <el-table
        :data="tableData"
        v-loading="tableLoading"
        element-loading-text="拼命加载中"
        element-loading-spinner="el-icon-loading"
        element-loading-background="rgba(0, 0, 0, 0.6)"
        max-height="600"
        :header-cell-style="{ background: '#fafafa', color: '#303133' }"
        stripe
      >
        <el-table-column prop="order_no" label="订单编号" minWidth="168"></el-table-column>
        <el-table-column label="被服务人" width="168">
          <template slot-scope="{ row: { user_name, user_tel }}">
            <div class="person-cell">
              <div class="person-name">{{ user_name || '—' }}</div>
              <div class="person-tel">{{ user_tel || '—' }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="订单状态" minWidth="120" align="center">
          <template slot-scope="{ row }">
            <el-select
              v-if="canChangeStatus && (row.order_status === 30 || row.order_status === 60)"
              v-model="row.order_status"
              size="small"
              style="width: 110px"
              @change="changeStatus(row.id, $event)"
            >
              <el-option label="服务中" :value="30"></el-option>
              <el-option label="已完成" :value="60"></el-option>
            </el-select>
            <el-tag v-else size="mini" :type="statusTagType(row.order_status)" effect="plain">{{ row.order_status | boardingStatusFilter }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="服务类型" minWidth="110" align="center">
          <template slot-scope="{ row }">
            <el-tag size="mini" type="info" effect="plain">{{ criTypeText(row.cri_type) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="护工姓名" minWidth="160">
          <template slot-scope="{ row: { nurse_name, nurse_tel }}">
            <div class="person-cell">
              <div class="person-name">{{ nurse_name || '—' }}</div>
              <div class="person-tel">{{ nurse_tel || '—' }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="地址" width="240">
          <template slot-scope="{ row: { hospital_name, address, town }}">
            <div class="address-cell">
              <span v-if="town">{{ town }}</span>
              <span v-if="hospital_name">/ {{ hospital_name }}</span>
              <span v-if="address">/ {{ address }}</span>
              <span v-if="!town && !hospital_name && !address" class="empty-text">—</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column minWidth="120" label="创建时间" align="center">
          <template slot-scope="{ row: { created_at }}">
            <span v-if="created_at">{{ (created_at * 1000) | formatDate('yyyy-MM-dd') }}</span>
            <span v-else class="empty-text">—</span>
          </template>
        </el-table-column>
        <el-table-column minWidth="130" label="最近一次服务时间" align="center">
          <template slot-scope="{ row: { last_log_time }}">
            <span v-if="last_log_time">{{ (last_log_time * 1000) | formatDate('yyyy-MM-dd') }}</span>
            <span v-else class="empty-text">—</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" fixed="right" width="150" align="center">
          <template slot-scope="scope">
            <el-link type="primary" icon="el-icon-tickets" @click="handleDetails(scope.row.id)" class="op-link">详情</el-link>
            <el-link v-if="canDeleteOrder" type="danger" icon="el-icon-delete" @click="handleDelete(scope.row.id, scope.row.order_no)" class="op-link">删除</el-link>
          </template>
        </el-table-column>
      </el-table>
      <CreateOrder ref="CreateOrder" @success="getList" />
      <div class="block">
        <el-pagination
          background
          :page-sizes="[10, 20, 50, 100]"
          :page-size="page_size"
          :current-page="page_number"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { fetchBoardingList } from '@/api'
import { formatDate } from 'element-ui/lib/utils/date-util'
import { delOrderById, switchStatusLeave } from '../../api'
import { getBoardingChildRoleIds, isAdmin } from '../../utils/util'
import { confirmDeleteOrder } from '@/utils/confirm-delete'
import CreateOrder from './components/CreateOrder'

// 寄宿托养订单状态：待接单(10) 统一按「待服务」展示
export const BoardingOrderStatus = [
  { label: '待服务', val: 10 },
  { label: '服务中', val: 30 },
  { label: '已完成', val: 60 },
  { label: '已取消', val: 50 }
]
const BoardingStatusMap = {
  1: '待服务',
  2: '已失效',
  10: '待服务',
  20: '待服务',
  30: '服务中',
  40: '待处理',
  50: '已取消',
  60: '已完成',
  41: '拒绝退款'
}

export default {
  name: 'OrdersBoarding',
  components: { CreateOrder },
  data() {
    return {
      BoardingOrderStatus,
      criRoleArr: getBoardingChildRoleIds(this.$store.state.rules),
      isAddNurse: false,
      fileList: [],
      search: {
        rangeDate: [],
        serveRange: [],
        user_name_or_tel: '',
        nurse_name_or_tel: '',
        order_status: '',
        cri_type: ''
      },
      tableLoading: false,
      page_size: 10,
      page_number: 1,
      total: 0,
      service_num: 0,
      pickerOptions: {
        shortcuts: [
          {
            text: '最近一周',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近一个月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近三个月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
              picker.$emit('pick', [start, end])
            }
          }
        ]
      },
      tableData: []
    }
  },
  filters: {
    formatDate,
    boardingStatusFilter(val) {
      return BoardingStatusMap[val] || '-'
    },
    nurseNameTel(val) {
      const { nurse_name, nurse_tel } = val
      let str = ''

      if (nurse_name && nurse_name !== '') {
        str += nurse_name
      } else {
        str += '-'
      }
      if (nurse_tel && nurse_tel !== '') {
        str += '/'
        str += nurse_tel
      } else {
        str += '/'
        str += '-'
      }
      return str
    }
  },
  computed: {
    isAdmin() {
      return isAdmin()
    },
    canCreateOrder() {
      return this.isAdmin || this.criRoleArr.includes('1')
    },
    canChangeStatus() {
      return this.isAdmin || this.criRoleArr.includes('2')
    },
    canDeleteOrder() {
      return this.isAdmin || this.criRoleArr.includes('3')
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    formatDate,
    criTypeText(val) {
      const map = { 1: '日间照料', 2: '寄宿托养' }
      return map[val] || '—'
    },
    statusTagType(status) {
      const map = { 1: 'warning', 10: 'info', 20: 'info', 30: 'primary', 60: 'success', 50: 'danger', 41: 'danger', 40: 'warning', 2: 'info' }
      return map[status] || 'info'
    },
    handleCreateOrder() {
      this.$refs.CreateOrder.open()
    },
    changeStatus(id, status) {
      const params = { order_id: id, status }
      switchStatusLeave(params).then(res => {
        if (res.status) {
          this.$message.success(res.msg || '修改成功')
          this.getList()
        } else {
          this.$message.error(res.msg || '修改失败')
          this.getList()
        }
      })
    },
    init() {
      this.getList()
    },
    resetForm(formName) {
      this.$refs[formName].resetFields()
      this.page_number = 1
      this.getList()
    },
    handleSizeChange(val) {
      console.log(`每页 ${val} 条`)
      this.page_size = val
      this.getList()
    },
    handleCurrentChange(val) {
      console.log(`当前页: ${val}`)
      this.page_number = val
      this.getList()
    },
    searchData() {
      this.page_number = 1
      this.getList()
    },
    getList() {
      this.tableLoading = true
      const {
        page_size, page_number, search
      } = this
      const { rangeDate, serveRange, ...searchInfo } = search
      const params = {
        pageNumber: page_number,
        pageSize: page_size,
        ...searchInfo
      }
      if (rangeDate && rangeDate.length === 2) {
        params.create_time = `${Math.floor(Number(rangeDate[0]) / 1000)},${Math.floor(Number(rangeDate[1]) / 1000)}`
      }
      if (serveRange && serveRange.length === 2) {
        // 与 create_time 一致：单个字段，值为「开始秒,结束秒」
        params.last_log_time = `${Math.floor(Number(serveRange[0]) / 1000)},${Math.floor(Number(serveRange[1]) / 1000)}`
      }
      fetchBoardingList(params)
        .then(res => {
          if (res.status) {
            this.tableData = res.data.list || []
            this.total = res.data.total || 0
            this.service_num = res.data.served_num || 0
          } else {
            this.tableData = []
          }
        })
        .finally(() => {
          this.tableLoading = false
        })
    },
    handleDetails(_RowId) {
      this.$router.push({
        path: '/orders_boarding_detail',
        query: {
          id: _RowId,
          type: 'orders_boarding'
        }
      })
    },

    handleDelete(order_id, orderNo) {
      confirmDeleteOrder(this, `确定删除订单${orderNo}吗?`)
        .then(() => {
          delOrderById({
            order_id
          }).then(res => {
            if (res.status) {
              this.$message({
                type: 'success',
                message: '删除成功!'
              })
              this.getList()
            } else {
              this.$message({
                type: 'warning',
                message: res.msg
              })
            }
          })
        })
        .catch(() => {
          console.log('取消删除')
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.boarding-page {
  padding: 16px 20px 30px;
}
.page-header {
  padding-bottom: 14px;
  border-bottom: 1px solid #ececec;
}
.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}
.mt20 { margin-top: 20px; }
.mt15 { margin-top: 15px; }
.info-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 16px 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
}
.info-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  border-bottom: 1px solid #f2f6fc;
}
.info-card-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  position: relative;
  padding-left: 10px;
}
.info-card-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 14px;
  background: #26a4b2;
  border-radius: 2px;
}
.person-cell { line-height: 1.6; }
.person-name { color: #303133; font-weight: 500; }
.person-tel { color: #909399; font-size: 13px; }
.address-cell { color: #606266; line-height: 1.6; }
.empty-text { color: #c0c4cc; }
.op-link { margin-right: 12px; }
.block {
  text-align: center;
  margin-top: 20px;
}
</style>
